import React, { Fragment, useContext, useState, useEffect } from "react";
import PlayerList from "./players/PlayerList";
import PlayCreate from "./plays/PlayCreate";
import PlayList from "./plays/PlayList";
import Scoreboard from "./Scoreboard";
import TeamStatsEdit from "./teamStats/TeamStatsEdit";
import GameActions from "./GameActions";
import GameStatus from "./GameStatus";
import MuiAlert from "@material-ui/lab/Alert";
import { PlayByPlayContext } from "games/model/PlayByPlayContext";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  TabbedForm,
  FormTab,
  BooleanInput,
  Button,
  Toolbar,
  DeleteButton,
  TextInput,
  TextField,
  DateInput,
  ReferenceField,
  SelectInput,
  required
} from "react-admin";
import { makeStyles } from "@material-ui/core/styles";
import _ from "lodash";
import { useFormState } from "react-final-form";
import LocationReferenceInput from "./LocationReferenceInput";
import TimeInput from "../fields/TimeInput";
import GenerateArticleToggleField from "../fields/GenerateArticleToggleField";
import { useLocation } from "react-router";
import { convertNum, isDisplayable } from "util/misc";
import { getTeamCode } from "games/utils";

const useStyles = makeStyles({
  field: {
    width: "100%",
    marginBottom: "10px"
  },
  message: {
    width: "100%"
  }
});

// help more tabs fit on the screen
// className prop not working for FormTab component
// so using inline styles instead
const formTabStyles = {
  fontSize: "13px",
  minWidth: "150px"
};

// Using wrapper to access pristine variable.
// Not the cleanest -- but could not find a way to access it otherwise
const ActionFormTabWrapper = (props) => {
  // react-admin's pristine tracking
  const { pristine } = useFormState();
  // pristine tracking for plays / stats -- is different than above
  // bc plays/stats do not use react-admin fields
  const { isPristine } = useContext(PlayByPlayContext);
  return (
    <FormTab
      label="Actions"
      id={isPristine && pristine ? "" : "disabledActionsFormTab"} // for some reason className was not working so I used id instead -- see index.scss
      onClick={props.handleTabClick}
      style={formTabStyles}
      {...props}
    >
      <GameActions {...props} />
    </FormTab>
  );
};

const GameEditToolbar = (props) => {
  const {
    // eslint-disable-next-line no-unused-vars
    usePlayByPlay: [state, dispatch]
  } = useContext(PlayByPlayContext);
  const { modified, values } = useFormState();

  // using "modified" to get a list of all the react-admin fields,
  // ignoring if they are actually modified or not.
  // allows to pass the react-admin fields/values into the custom save function that also handles pxp logic
  // instead of using react-admin's built in save functionaity
  // there may be a better way to do this.
  const keys = Object.keys(modified);
  let data = {};

  keys.forEach((key) => {
    data[key] = _.get(values, key);
  });

  const handleSave = () => {
    dispatch({
      type: "saveGame",
      payload: {
        shouldSave: true,
        data: data
      }
    });
  };

  return (
    // flex-end styling moves the "Save Game" button to the right of the page so
    // it isn't too close to / confused with the "Add New Play" button
    <Toolbar
      style={{ marginTop: "50px", justifyContent: "flex-end" }}
      {...props}
    >
      <Button label="Save Game" variant="contained" onClick={handleSave} />
      <DeleteButton undoable={false} redirect="/games" />
    </Toolbar>
  );
};

// is there a better way to do this?
const getCurrentTab = (location) => {
  const urlParts = _.get(location, "pathname", "").split("/");
  const lastUrlPart = _.get(urlParts, urlParts.length - 1);
  // assuming won't have more than 99 tabs. IDs are longer than 2 characters, so if the last url part is an id,
  // that means the location is on the first tab
  if (lastUrlPart.length < 3) {
    return convertNum(lastUrlPart);
  }
  return 0;
};

const GameEditFootball = (props) => {
  const {
    usePlayByPlay: [state],
    isLoading
  } = useContext(PlayByPlayContext);
  const { gameStatus, stats, game } = state;
  const styles = useStyles();
  const location = useLocation();
  const currentTab = getCurrentTab(location);
  const getIsCurrentTabMatch = (index) => currentTab === index;
  const [homeTeamStats, setHomeTeamStats] = useState({});
  const [awayTeamStats, setAwayTeamStats] = useState({});
  const [homePlayerStats, setHomePlayerStats] = useState([]);
  const [awayPlayerStats, setAwayPlayerStats] = useState([]);
  const homeTeamCode = getTeamCode("home", game);
  const awayTeamCode = getTeamCode("away", game);

  const setupStats = () => {
    const initialHomeTeamStats = _.get(stats, "home.teamStats");
    const initialAwayTeamStats = _.get(stats, "away.teamStats");
    const initialHomePlayerStats = _.get(stats, "home.playerStats");
    const initialAwayPlayerStats = _.get(stats, "away.playerStats");
    setHomeTeamStats(initialHomeTeamStats);
    setAwayTeamStats(initialAwayTeamStats);
    setHomePlayerStats(initialHomePlayerStats);
    setAwayPlayerStats(initialAwayPlayerStats);
  };

  useEffect(() => {
    if (!isDisplayable(stats)) return;
    setupStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats]);

  const handleTabClick = () => {
    setupStats();
  };

  const warningMessage = (
    <MuiAlert
      className={styles.message}
      severity="warning"
      elevation={6}
      variant="filled"
    >
      Editing plays will not update the game's stats because the game status is
      final.
    </MuiAlert>
  );

  return (
    <Fragment>
      <Scoreboard currentTab={currentTab} />
      <GameStatus />
      <TabbedForm {...props} toolbar={<GameEditToolbar />}>
        <FormTab
          label="Play by Play"
          style={formTabStyles}
          onClick={handleTabClick}
        >
          {getIsCurrentTabMatch(0) && (
            <Fragment>
              {gameStatus === "FINAL" && warningMessage}
              <PlayList />
              <PlayCreate />
            </Fragment>
          )}
        </FormTab>

        <FormTab
          label="Commentary"
          style={formTabStyles}
          onClick={handleTabClick}
        >
          <TextInput
            source="gameStory"
            label="Game Story"
            multiline
            rows={10}
            className={styles.field}
          />
          <BooleanInput
            label="Game Story Published"
            helperText="If published, the game story will appear on the site."
            source="gameStoryIsPublished"
          />
          <TextInput
            source="internalNotes"
            label="Internal Notes"
            multiline
            rows={6}
            className={styles.field}
          />
        </FormTab>

        <FormTab
          label={`${awayTeamCode} Team Stats`}
          style={formTabStyles}
          onClick={handleTabClick}
        >
          {getIsCurrentTabMatch(2) && (
            <TeamStatsEdit
              side="away"
              activeTeamStats={awayTeamStats}
              setActiveTeamStats={setAwayTeamStats}
              otherTeamStats={homeTeamStats}
              setOtherTeamStats={setHomeTeamStats}
            />
          )}
        </FormTab>

        <FormTab
          label={`${awayTeamCode} Player Stats`}
          style={formTabStyles}
          onClick={handleTabClick}
        >
          {getIsCurrentTabMatch(3) && (
            <PlayerList
              side="away"
              activePlayers={awayPlayerStats}
              setActivePlayers={setAwayPlayerStats}
            />
          )}
        </FormTab>

        <FormTab
          label={`${homeTeamCode} Team Stats`}
          style={formTabStyles}
          onClick={handleTabClick}
        >
          {getIsCurrentTabMatch(4) && (
            <TeamStatsEdit
              side="home"
              activeTeamStats={homeTeamStats}
              setActiveTeamStats={setHomeTeamStats}
              otherTeamStats={awayTeamStats}
              setOtherTeamStats={setAwayTeamStats}
            />
          )}
        </FormTab>

        <FormTab
          label={`${homeTeamCode} Player Stats`}
          style={formTabStyles}
          onClick={handleTabClick}
        >
          {getIsCurrentTabMatch(5) && (
            <PlayerList
              side="home"
              activePlayers={homePlayerStats}
              setActivePlayers={setHomePlayerStats}
            />
          )}
        </FormTab>

        <FormTab label="Info" style={formTabStyles} onClick={handleTabClick}>
          <TextField source="id" />
          <TextField source="seasonCode" label="Season" />
          <ReferenceField
            link={false}
            label="Sport"
            source="sportCode"
            reference="sportCodes"
          >
            <TextField source="label" />
          </ReferenceField>
          <DateInput
            label="Date"
            source="gameDateString"
            validate={[required()]}
          />
          <TimeInput label="Time" source="gameTimeString" />
          <BooleanInput label="Is District Game" source="gameIsDistrict" />
          <BooleanInput label="Is Top Game" source="isTopGame" />
          <SelectInput
            source="playoffRound"
            allowEmpty={true}
            choices={[
              { id: "BI_DISTRICT", name: "Bi-District" },
              { id: "AREA", name: "Area" },
              { id: "REGIONAL_QUARTERFINAL", name: "Regional Quaterfinal" },
              { id: "REGIONAL_SEMIFINAL", name: "Regional Semifinal" },
              { id: "REGIONAL_FINAL", name: "Regional Final" },
              { id: "STATE_SEMIFINAL", name: "State Semifinal" },
              { id: "STATE_FINAL", name: "State Final" }
            ]}
          />
          <LocationReferenceInput
            source="location"
            reference="locations"
            allowEmpty
          />
          <GenerateArticleToggleField
            source="metadata"
            label="Generate Article"
            helperText="Automatically generate an article for this game"
            defaultValue={{}}
          />
        </FormTab>

        <ActionFormTabWrapper handleTabClick={handleTabClick} />

        {isLoading && <CircularProgress />}
      </TabbedForm>
    </Fragment>
  );
};

export default GameEditFootball;
